import { Chip, Tooltip } from '@mui/material';
import type { EstadoBiblioteca } from '../lib/api.js';

/** Cuatro estados, y la diferencia entre ellos importa:
 *
 *  · Verde  — cruce exacto por IMDb. Es un hecho.
 *  · Azul   — la tienes, pero hay una versión mejor.
 *  · Gris   — cruce por título y año. NO es un hecho: "puede que la tengas".
 *  · Ámbar  — no se sabe: no hay bibliotecas marcadas.
 *
 *  Pintar un `fuzzy` como si fuera seguro llevaría a no descargar algo que sí falta. */
export function InsigniaBiblioteca({ estado }: { estado: EstadoBiblioteca }) {
  if (estado.status === 'unknown') {
    return (
      <Tooltip title="No hay ninguna biblioteca de Plex marcada, así que la app no puede saber qué películas tienes. Se configura en Ajustes.">
        <Chip size="small" color="warning" variant="outlined" label="Sin saber" />
      </Tooltip>
    );
  }
  if (estado.status === 'missing') return null;

  const donde = [estado.serverName, estado.libraryTitle].filter(Boolean).join(' / ');

  if (estado.confidence === 'fuzzy') {
    // En una serie el cruce por título es lo normal: con el agente antiguo de TheTVDB Plex
    // no da IMDb, y su año no es fiable (D-27). Se dice, y se cuenta lo que hay.
    if (estado.episodiosEnPlex != null) {
      return (
        <Tooltip title={`Coincidencia por título, no por identificador: Plex no publica el IMDb de esta serie. Comprueba en ${donde} que es la misma antes de fiarte de los episodios marcados.`}>
          <Chip
            size="small"
            variant="outlined"
            color="success"
            label={`Puede que la tengas · ${estado.episodiosEnPlex} ${estado.episodiosEnPlex === 1 ? 'episodio' : 'episodios'}`}
          />
        </Tooltip>
      );
    }
    return (
      <Tooltip title={`Coincidencia por título y año, no por identificador. Comprueba en ${donde} antes de fiarte.`}>
        <Chip size="small" variant="outlined" label="Puede que la tengas" />
      </Tooltip>
    );
  }
  // Una serie no se «tiene»: se tienen episodios. La ficha dice cuáles, uno a uno (D-27).
  if (estado.episodiosEnPlex != null) {
    return (
      <Tooltip title={`En ${donde}. La ficha marca qué episodios están y «Descargar temporada» pide solo los que faltan.`}>
        <Chip
          size="small"
          color="success"
          label={`En Plex · ${estado.episodiosEnPlex} ${estado.episodiosEnPlex === 1 ? 'episodio' : 'episodios'}`}
        />
      </Tooltip>
    );
  }
  if (estado.upgradeAvailable) {
    return (
      <Tooltip title={`La tienes en ${estado.resolution}p en ${donde}, y hay una versión de más resolución.`}>
        <Chip size="small" color="info" label={`Mejorable · tienes ${estado.resolution}p`} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={`En ${donde}${estado.resolution ? ` · ${estado.resolution}p` : ''}`}>
      <Chip size="small" color="success" label="Ya la tienes" />
    </Tooltip>
  );
}
